/**
 * Gestione ▸ Alert, by group — "one email per alert type" (owner ruling DEC-101, `docs/adr/9040`).
 *
 * READS
 *   `GET /api/v1/foundations/exceptions/groups?group_by=&queue&state&overdue&rule_id&country_key&limit=500`
 *   — every page, `nextCursor` followed to the end (at most {@link ALERT_GROUP_MAX_PAGES}) — through `ALERT_GROUPS_READ` — `null` until the pinned spec carries it
 *   (`src/api/pending/alert-groups.ts`); then the page says «In arrivo» once and draws the silhouette.
 *   `GET /foundations/exception-rules` names the rules for the rule filter.
 *
 * ONE ROW PER GROUP, ONE LINE (ADR 9028): severity · the alert type (the rule's catalogue name, or
 * its id in mono) · the market, store or department · «N casi» · stores and articles · the oldest
 * («da 2 giorni») · overdue, in red when any · who has it · Apri, Assegna, Rinvia. EVERY group is
 * read, then ranked by the planner (ADR 9034: severity by default — the platform's own order,
 * severity then overdue then count — or overdue, cases, oldest, unassigned; the place filtered by
 * text); the top fifty are drawn under «Prime 50 di N», and the CSV/Excel carry every group that
 * matches. The total is said in the sentence: «{groups} gruppi, {alerts} alert; {overdue} in
 * ritardo». Until 2026-10-02 only the first page of fifty was read, so by store the sentence said
 * 413 groups over fifty rows with no note and no way to the rest, and the exports held fifty.
 *
 * GROUPED BY a dimension the planner chooses — «Raggruppa per: Mercato · Negozio · Reparto»,
 * `?by=`, market by default. A department is one market's (ADR 2106): with no `?country=` the
 * market is asked for on the same line, and nothing is read until it is chosen (DEC-85 — no
 * market is chosen for the planner; ADR 9039 defaults runs only).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { CircleAlert } from 'lucide-react';

import { callOperation, useApiCollection, useApiQuery } from '@/api';
import { inseasonListRuns, listExceptionRules, listFeeds, type AlertState } from '@/api/generated';
import { formatInteger } from '@/lib/format';
import { AboutPopover, Button, Select, StatusIndicator, Switch } from '@/primitives';
import {
  ArrivingTag, GroupByControl, GroupedQueueGhost, GroupedQueueList, IdTag, InAppAnchor, MarketDefaultNote, PageKicker, PageStatusRegion,
  PersonName, personText, replaceSearchParam, SentenceLine, useLocationSearch, useMarketDefault, usePageStatusEntry, useRankedCap,
  type DataColumn, type RankedCapConfig, type TemplateSentence, type ThresholdClass,
} from '@/planning';

import { toRuleKey, toThresholdClass, UNCLASSIFIED_RULE_KEY } from './api';
import type { AlertGroup, AlertGroupActionOp, AlertGroupPage, AlertGroupsRead } from '@/api/pending/alert-groups';
import { ALERT_GROUP_DIMENSIONS, type AlertGroupDimension } from '@/api/pending/alert-groups';
import {
  ALERT_GROUP_MAX_PAGES, ALERT_GROUP_PAGE_SIZE, BY_PARAM, RULE_PARAM, VIEW_PARAM, alertGroupHref, alertGroupInboxQuery,
  daysSince, inboxGroupFilter,
  inboxNeedsMarket, readAlertGroupInboxScope, type AlertGroupFilters, type AlertGroupInboxScope, type AlertView,
} from './alert-groups';
import { AlertGroupActButtons } from './alert-group-acts';
import {
  ALERT_QUEUES, ALERT_STATES, ALL_OPTION, COUNTRY_PARAM, OVERDUE_PARAM, QUEUE_LABEL_KEY, QUEUE_PARAM, STATE_LABEL_KEY,
  STATE_PARAM,
} from './alert-queue';
import { countryChoices } from './data-schedules';
import { setFoundationsScopeParam } from './scope';

/** Each severity class's words — spelled out so the catalogue check sees every key. */
export const SEVERITY_LABEL_KEY: Readonly<Record<ThresholdClass, string>> = {
  good: 'foundations.alerts.severity.good',
  warning: 'foundations.alerts.severity.warning',
  serious: 'foundations.alerts.severity.serious',
  critical: 'foundations.alerts.severity.critical',
};

/** Each dimension's words in the «Raggruppa per» control. */
export const DIMENSION_LABEL_KEY: Readonly<Record<AlertGroupDimension, string>> = {
  country: 'foundations.alertGroups.by.country',
  store: 'foundations.alertGroups.by.store',
  department: 'foundations.alertGroups.by.department',
};

/** The rule's catalogue name, or `null` when this build names none — the id is shown instead, in mono. */
export function ruleNameKey(ruleId: string): string | null {
  const key = toRuleKey(ruleId);
  return key === UNCLASSIFIED_RULE_KEY ? null : key;
}

/**
 * The alert type: its name — or, for a rule this build has no words for, a plain sentence with the
 * rule's id one press away in the «id» (audit A5, 2026-10-10).
 */
export function RuleName({ ruleId }: { readonly ruleId: string }): React.JSX.Element {
  const { t } = useTranslation();
  const key = ruleNameKey(ruleId);
  return key === null
    ? (
      <span className="inline-flex flex-wrap items-baseline gap-1" data-rule-id="">
        <span>{t('foundations.rule.unclassified')}</span>
        <IdTag id={ruleId} kind={t('foundations.rule.kind')} />
      </span>
    )
    : <span data-rule-id={ruleId}>{t(key)}</span>;
}

const CELL = 'py-1 pe-3 align-middle whitespace-nowrap';
const NUMBER = `${CELL} font-numeric text-numeric text-end tabular-nums`;

/** «Per gruppi · Singoli alert» — both views of the one inbox, on the link (`?view=`). */
export function AlertViewSwitch({ view, groupsPinned }: { readonly view: AlertView; readonly groupsPinned: boolean }): React.JSX.Element {
  const { t } = useTranslation();
  const views: readonly AlertView[] = ['groups', 'alerts'];
  return (
    <div role="group" aria-label={t('foundations.alertGroups.view.label')} className="flex flex-wrap items-center gap-2" data-alert-view={view} data-tour="foundations-exceptions-view">
      {views.map((option) => (
        <Button
          key={option}
          size="sm"
          variant={view === option ? 'primary' : 'secondary'}
          aria-pressed={view === option}
          onClick={() => setFoundationsScopeParam(VIEW_PARAM, option)}
        >
          {t(option === 'groups' ? 'foundations.alertGroups.view.groups' : 'foundations.alertGroups.view.alerts')}
        </Button>
      ))}
      {!groupsPinned && <ArrivingTag />}
      <AboutPopover aboutKeys={['foundations.help.alertGroupsInbox.view']} title={t('foundations.alertGroups.view.label')} size="sm" />
    </div>
  );
}

/** The markets a planner may pick: those the platform's reads name, the linked one included;
 *  `settled` once both reads have answered. */
function useMarkets(linked: string | null): { readonly markets: readonly string[]; readonly settled: boolean } {
  const feeds = useApiCollection(listFeeds, { query: {} });
  const runs = useApiCollection(inseasonListRuns, { query: {} });
  const markets = useMemo(
    () => countryChoices(
      [...(feeds.data ?? []).map((feed) => feed.countryKey), ...(runs.data ?? []).map((run) => run.country_key)],
      linked,
    ),
    [feeds.data, runs.data, linked],
  );
  return { markets, settled: feeds.isSuccess && runs.isSuccess };
}

/**
 * A market select, its label hidden (ADR 9036). `required`: no «every market» option — and then,
 * when the person can see exactly one market, it is opened and said (ADR 9056). As a filter
 * («every market» offered) nothing is chosen: no market is already a complete answer.
 */
export function MarketSelect({ value, required }: { readonly value: string | null; readonly required: boolean }): React.JSX.Element {
  const { t } = useTranslation();
  const { markets, settled } = useMarkets(value);
  const chosen = useMarketDefault({
    key: 'foundations:alert-groups',
    linked: value,
    visible: settled ? markets : undefined,
    write: (market) => replaceSearchParam(COUNTRY_PARAM, market),
    enabled: required,
  });
  return (
    <div className="flex min-w-[10rem] flex-col gap-1" data-market-select="">
      <Select
        labelKey="foundations.alerts.market.label"
        labelHidden
        {...(required ? { placeholderKey: 'foundations.alertGroups.market.choose' } : {})}
        options={[
          ...(required ? [] : [{ value: ALL_OPTION, label: t('foundations.alerts.market.all') }]),
          ...markets.map((market) => ({ value: market, label: market })),
        ]}
        {...(value === null ? (required ? {} : { value: ALL_OPTION }) : { value })}
        onValueChange={(next) => setFoundationsScopeParam(COUNTRY_PARAM, next === ALL_OPTION ? null : next)}
      />
      {required && <MarketDefaultNote chosen={chosen} market={value} />}
    </div>
  );
}

/** «Raggruppa per: Mercato · Negozio · Reparto» — the grouped queue's line (ADR 9036, ADR 9042). */
function GroupByLine({ scope }: { readonly scope: AlertGroupInboxScope }): React.JSX.Element {
  return (
    <GroupByControl
      titleKey="foundations.alertGroups.by.title"
      aboutKey="foundations.alertGroups.by.about"
      dimensions={ALERT_GROUP_DIMENSIONS}
      labelKey={DIMENSION_LABEL_KEY}
      value={scope.by}
      onChoose={(dimension) => setFoundationsScopeParam(BY_PARAM, dimension)}
    >
      {scope.by === 'department' && <MarketSelect value={scope.country} required />}
    </GroupByControl>
  );
}

/** Queue, state, overdue and rule — shared with the groups read and carried into a group. */
function GroupFilters({ scope, disabled }: { readonly scope: AlertGroupInboxScope; readonly disabled: boolean }): React.JSX.Element {
  const { t } = useTranslation();
  const rules = useApiQuery(listExceptionRules, {});
  const ruleIds = (rules.data ?? []).map((rule) => rule.ruleId);
  const offered = scope.ruleId === null || ruleIds.includes(scope.ruleId) ? ruleIds : [...ruleIds, scope.ruleId];
  return (
    <div role="group" aria-label={t('foundations.alerts.filters')} aria-disabled={disabled} data-group-filters="" data-tour="foundations-exceptions-filters" className="flex flex-wrap items-end gap-3">
      <div className="flex flex-wrap gap-2" role="group" aria-label={t('foundations.alerts.queue.label')}>
        {ALERT_QUEUES.map((queue) => (
          <Button
            key={queue}
            size="sm"
            disabled={disabled}
            variant={scope.queue === queue ? 'primary' : 'secondary'}
            aria-pressed={scope.queue === queue}
            onClick={() => setFoundationsScopeParam(QUEUE_PARAM, queue)}
          >
            {t(QUEUE_LABEL_KEY[queue])}
          </Button>
        ))}
      </div>
      <div className="max-w-xs">
        <Select
          labelKey="foundations.alerts.state.label"
          disabled={disabled}
          options={ALERT_STATES.map((value: AlertState) => ({ value, label: t(STATE_LABEL_KEY[value]) }))}
          value={scope.state}
          onValueChange={(value) => setFoundationsScopeParam(STATE_PARAM, value)}
        />
      </div>
      <div className="max-w-xs">
        <Select
          labelKey="foundations.alertGroups.rule.label"
          disabled={disabled}
          options={[
            { value: ALL_OPTION, label: t('foundations.alertGroups.rule.all') },
            ...offered.map((ruleId) => {
              const key = ruleNameKey(ruleId);
              return { value: ruleId, label: key === null ? ruleId : t(key) };
            }),
          ]}
          value={scope.ruleId ?? ALL_OPTION}
          onValueChange={(value) => setFoundationsScopeParam(RULE_PARAM, value === ALL_OPTION ? null : value)}
        />
      </div>
      <Switch
        labelKey="foundations.alerts.overdueOnly"
        checked={scope.overdueOnly}
        disabled={disabled}
        onCheckedChange={(next) => setFoundationsScopeParam(OVERDUE_PARAM, next ? 'true' : null)}
      />
      {scope.by !== 'department' && <MarketSelect value={scope.country} required={false} />}
    </div>
  );
}

/** Who holds the group's alerts: the first holder and how many more, or «nessuno». */
export function holdersWords(group: AlertGroup, t: TFunction): string {
  const [first, ...rest] = group.topAssignees;
  if (first === undefined) return t('foundations.alertGroups.holders.nobody');
  const name = personText(t, first.assignee, first.assigneeName, undefined, first.assigneeNameAbsentReason);
  return rest.length === 0
    ? t('foundations.alertGroups.holders.one', { name, count: first.count })
    : t('foundations.alertGroups.holders.more', { name, count: first.count, others: rest.length });
}

/**
 * Who holds the group's alerts, drawn: the first holder by name — or by id, in mono, when the
 * platform has no name for them (DEC-102), or in words with the id behind the «id» button where it says why
 * (ADR 2127) — and how many more; «nessuno» when nobody does.
 */
function Holders({ group }: { readonly group: AlertGroup }): React.JSX.Element {
  const { t } = useTranslation();
  const [first, ...rest] = group.topAssignees;
  if (first === undefined) return <span>{t('foundations.alertGroups.holders.nobody')}</span>;
  return (
    <span className="inline-flex max-w-[20rem] items-baseline gap-1 truncate" title={holdersWords(group, t)} data-group-holders="">
      <PersonName subject={first.assignee} name={first.assigneeName} reason={first.assigneeNameAbsentReason} />
      <span>{t(rest.length === 0 ? 'foundations.alertGroups.holders.count' : 'foundations.alertGroups.holders.countMore', { count: first.count, others: rest.length })}</span>
    </span>
  );
}

/** How severe each class is, for the ranking: critical first. */
/** A store group's name — the platform's `dimensionLabel` (group_by=store); `null` otherwise or when the master has none. */
export function groupLabel(group: Pick<AlertGroup, 'dimensionLabel'>): string | null {
  const label = group.dimensionLabel?.trim() ?? '';
  return label === '' ? null : label;
}

/** «Santafé (1203)» for a named store group, the key alone otherwise; `null` for the no-value bucket. */
export function groupWhereText(group: Pick<AlertGroup, 'dimensionKey' | 'dimensionLabel'>, t: TFunction): string | null {
  const label = groupLabel(group);
  if (group.dimensionKey === null) return null;
  return label === null ? group.dimensionKey : t('alertNames.withCode', { name: label, code: group.dimensionKey });
}

const SEVERITY_RANK: Readonly<Record<ThresholdClass, number>> = { critical: 3, serious: 2, warning: 1, good: 0 };

/**
 * The inbox's ranking over every group read (ADR 9034). The default, severity then overdue, is
 * the platform's own order (severity, overdue, count) — ties keep the order read, which is the
 * platform's — so a planner who touches nothing sees what the platform put first.
 *
 * **The place COLLECTS values** (client request, 2026-10-06: a filter is not a free-text box).
 * It matched text over the label and the key joined, which was how one box could find either —
 * and which meant a planner wanting two stores could name neither. The filter is now over the
 * dimension key, with the places offered as «Santafé (1203)» through the same `groupWhereText`
 * the column uses, so the label is what a planner reads and the URL stays the key.
 */
export function alertGroupRankConfig(t: TFunction, groups: readonly AlertGroup[] = []): RankedCapConfig<AlertGroup> {
  const places = new Map<string, string>();
  for (const group of groups) {
    const text = groupWhereText(group, t);
    if (group.dimensionKey !== null && text !== null) places.set(group.dimensionKey, text);
  }
  return {
    namespace: 'groups',
    defaultRank: 'severity',
    rankOptions: [
      {
        id: 'severity', labelKey: 'foundations.alertGroups.rank.severity', direction: 'desc',
        value: (group) => SEVERITY_RANK[toThresholdClass(group.severity)],
        tieBreak: { value: (group) => group.overdueCount, direction: 'desc' },
      },
      {
        id: 'overdue', labelKey: 'foundations.alertGroups.rank.overdue', direction: 'desc',
        value: (group) => group.overdueCount,
        tieBreak: { value: (group) => group.alertCount, direction: 'desc' },
      },
      { id: 'cases', labelKey: 'foundations.alertGroups.rank.cases', direction: 'desc', value: (group) => group.alertCount },
      { id: 'oldest', labelKey: 'foundations.alertGroups.rank.raised', direction: 'asc', value: (group) => group.oldestRaisedAt },
      { id: 'unassigned', labelKey: 'foundations.alertGroups.rank.unassigned', direction: 'desc', value: (group) => group.unassignedCount },
    ],
    filters: [
      {
        id: 'where', labelKey: 'foundations.alertGroups.column.where', kind: 'multi',
        value: (group) => group.dimensionKey, label: (key) => places.get(key) ?? key,
      },
    ],
  };
}

/** Every page of the groups read, joined — and whether the walk stopped at its bound. */
export interface AlertGroupsReadAll {
  readonly items: readonly AlertGroup[];
  readonly totals: AlertGroupPage['totals'];
  readonly partial: boolean;
}

/**
 * Read every group the inbox's filters match: `nextCursor` followed to the end, at most
 * {@link ALERT_GROUP_MAX_PAGES} pages of {@link ALERT_GROUP_PAGE_SIZE}. The totals are the first
 * page's — the platform's count over every group the filters match. Pure over `call`, for the test.
 */
export async function readAllAlertGroups(
  scope: AlertGroupInboxScope,
  call: (query: NonNullable<ReturnType<typeof alertGroupInboxQuery>>) => Promise<AlertGroupPage>,
): Promise<AlertGroupsReadAll> {
  const items: AlertGroup[] = [];
  let totals: AlertGroupPage['totals'] | null = null;
  let cursor: string | undefined;
  for (let page = 0; page < ALERT_GROUP_MAX_PAGES; page += 1) {
    const query = alertGroupInboxQuery(scope, ALERT_GROUP_PAGE_SIZE, cursor);
    if (query === null) throw new Error('the inbox reads nothing while a department grouping has no market');
    const answer = await call(query);
    items.push(...answer.items);
    totals ??= answer.totals;
    if (answer.nextCursor === null) return { items, totals, partial: false };
    cursor = answer.nextCursor;
  }
  return { items, totals: totals!, partial: true };
}

/** The inbox's sentence: the platform's totals over every group the filters match, not the page's. */
export function inboxSentence(page: Pick<AlertGroupPage, 'totals'>): TemplateSentence {
  const { groups, alerts, overdue } = page.totals;
  return { key: 'foundations.alertGroups.sentence.all', values: { groups, alerts, overdue } };
}

function useGroupColumns(filters: AlertGroupFilters, act: AlertGroupActionOp | null, now: number): readonly DataColumn<AlertGroup>[] {
  const { t, i18n } = useTranslation();
  const n = (value: number): string => formatInteger(value, i18n.language);
  return [
    {
      id: 'severity', headerKey: 'foundations.column.severity', descriptionKey: 'foundations.help.alertGroupsInbox.colSeverity', kind: 'enum', ghost: 'tag',
      value: (group) => toThresholdClass(group.severity),
      label: (value) => t(SEVERITY_LABEL_KEY[value as ThresholdClass] ?? SEVERITY_LABEL_KEY.serious),
      cell: (group) => {
        const level = toThresholdClass(group.severity);
        return <td className={CELL}><StatusIndicator level={level} labelKey={SEVERITY_LABEL_KEY[level]} /></td>;
      },
    },
    {
      id: 'rule_id', headerKey: 'foundations.alertGroups.column.type', descriptionKey: 'foundations.help.alertGroupsInbox.colType', kind: 'text',
      value: (group) => group.ruleId,
      cell: (group) => <th scope="row" className={`${CELL} text-start font-semibold`}><RuleName ruleId={group.ruleId} /></th>,
    },
    {
      id: 'dimension_key', headerKey: 'foundations.alertGroups.column.where', descriptionKey: 'foundations.help.alertGroupsInbox.colWhere', kind: 'text', code: true,
      // A store group by its name (usability audit B4): the platform's `dimensionLabel`, with the key.
      value: (group) => groupWhereText(group, t),
      cell: (group) => {
        const label = groupLabel(group);
        if (group.dimensionKey !== null && label !== null) {
          return <td className={CELL} data-group-label={group.dimensionKey}>{t('alertNames.withCode', { name: label, code: group.dimensionKey })}</td>;
        }
        return (
          <td className={`${CELL} font-numeric`}>
            {group.dimensionKey ?? t('foundations.alertGroups.noDimension', { dimension: t(DIMENSION_LABEL_KEY[group.dimension]) })}
          </td>
        );
      },
    },
    {
      id: 'alert_count', headerKey: 'foundations.alertGroups.column.cases', descriptionKey: 'foundations.help.alertGroupsInbox.colCases', kind: 'number', align: 'end', emphasis: true,
      value: (group) => group.alertCount,
      cell: (group) => <td className={NUMBER} data-group-cases="">{t('foundations.alertGroups.cases', { count: group.alertCount, n: n(group.alertCount) })}</td>,
    },
    {
      id: 'store_count', headerKey: 'foundations.alertGroups.column.spread', descriptionKey: 'foundations.help.alertGroupsInbox.colSpread', kind: 'number', align: 'end',
      value: (group) => group.storeCount,
      cell: (group) => (
        <td className={NUMBER}>
          {t('foundations.alertGroups.spread', { stores: group.storeCount, articles: group.articleCount, s: n(group.storeCount), a: n(group.articleCount) })}
        </td>
      ),
    },
    {
      id: 'oldest_raised_at', headerKey: 'foundations.alertGroups.column.oldest', descriptionKey: 'foundations.help.alertGroupsInbox.colOldest', kind: 'date',
      value: (group) => group.oldestRaisedAt,
      cell: (group) => <td className={CELL} title={group.oldestRaisedAt}>{t('foundations.alertGroups.since', { days: daysSince(group.oldestRaisedAt, now) })}</td>,
    },
    {
      id: 'overdue_count', headerKey: 'foundations.alertGroups.column.overdue', descriptionKey: 'foundations.help.alertGroupsInbox.colOverdue', kind: 'number', align: 'end',
      value: (group) => group.overdueCount,
      cell: (group) => (
        <td className={NUMBER} data-group-overdue={group.overdueCount > 0 ? 'late' : 'none'}>
          {group.overdueCount > 0 ? (
            <span className="text-status-critical-strong inline-flex items-center gap-1 font-semibold">
              <CircleAlert aria-hidden className="size-[14px] shrink-0" />
              {t('foundations.alertGroups.overdue', { count: group.overdueCount, n: n(group.overdueCount) })}
            </span>
          ) : n(0)}
        </td>
      ),
    },
    {
      id: 'holders', headerKey: 'foundations.alertGroups.column.holders', descriptionKey: 'foundations.help.alertGroupsInbox.colHolders', kind: 'text',
      value: (group) => holdersWords(group, t),
      cell: (group) => <td className={CELL}><Holders group={group} /></td>,
    },
    {
      id: 'actions', headerKey: 'foundations.alertGroups.column.actions', descriptionKey: 'foundations.help.alertGroupsInbox.colActions',
      cell: (group) => <td className={CELL}><GroupRowActions group={group} filters={filters} act={act} /></td>,
    },
  ];
}

function GroupRowActions({ group, filters, act }: {
  readonly group: AlertGroup; readonly filters: AlertGroupFilters; readonly act: AlertGroupActionOp | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const href = alertGroupHref(group, filters);
  const filter = inboxGroupFilter(group, filters);
  if (href === null || filter === null) {
    return <span className="text-caption text-ink-secondary">{t('foundations.alertGroups.notOpenable')}</span>;
  }
  return (
    <span className="flex items-center gap-2">
      <InAppAnchor href={href} className="text-ink-primary text-[13px] font-semibold underline" data-group-open="">
        {t('foundations.alertGroups.open')}
      </InAppAnchor>
      {act !== null && (
        <AlertGroupActButtons
          act={act}
          compact
          kinds={['assign', 'snooze']}
          target={{ filter, count: group.alertCount, country: group.countryKey }}
        />
      )}
    </span>
  );
}

/**
 * The inbox's kicker, «Gestione · Eccezioni», for BOTH of its views. It belongs to the route, not
 * to a view (issue 249): set by the grouped view alone, switching to the per-alert list unmounted
 * it, its cleanup cleared the override, and the header fell back to the chapter's «Fondamenta».
 */
export function ExceptionsKicker(): React.JSX.Element {
  const { t } = useTranslation();
  return <PageKicker text={t('foundations.parameters.kicker', { app: t('nav.app.management'), page: t('foundations.nav.exceptions') })} />;
}

/** The inbox over a given read and act — `null` while the pinned client has none. */
export function AlertGroupsInbox({ read, act, now = Date.now() }: {
  readonly read: AlertGroupsRead | null;
  readonly act: AlertGroupActionOp | null;
  readonly now?: number;
}): React.JSX.Element {
  const search = useLocationSearch();
  const scope = useMemo(() => readAlertGroupInboxScope(search), [search]);
  return (
    <PageStatusRegion>
      <div className="flex flex-col gap-6" data-alert-groups="">
        <AlertViewSwitch view="groups" groupsPinned={read !== null} />
        <div data-tour="foundations-exceptions-group-by"><GroupByLine scope={scope} /></div>
        <GroupFilters scope={scope} disabled={read === null} />
        {read === null
          ? <NotPublishedGroups scope={scope} now={now} />
          : inboxNeedsMarket(scope)
            ? <SentenceLine sentence={{ key: 'foundations.alertGroups.sentence.needsMarket' }} />
            : <GroupList read={read} act={act} scope={scope} now={now} />}
      </div>
    </PageStatusRegion>
  );
}

function NotPublishedGroups({ scope, now }: { readonly scope: AlertGroupInboxScope; readonly now: number }): React.JSX.Element {
  usePageStatusEntry({
    kind: 'not-published',
    message: { key: 'foundations.alertGroups.notPublished.title' },
    detail: { key: 'foundations.alertGroups.notPublished.detail' },
  });
  const columns = useGroupColumns(scope, null, now);
  return (
    <>
      <SentenceLine sentence={{ key: 'foundations.alertGroups.sentence.arriving' }} />
      <GroupedQueueGhost rowKey={(group: AlertGroup) => group.groupKey} columns={columns} captionKey="foundations.alertGroups.caption" tourAnchor="foundations-exceptions-groups" />
    </>
  );
}

function GroupList({ read, act, scope, now }: {
  readonly read: AlertGroupsRead;
  readonly act: AlertGroupActionOp | null;
  readonly scope: AlertGroupInboxScope;
  readonly now: number;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const query = alertGroupInboxQuery(scope, ALERT_GROUP_PAGE_SIZE);
  const groups = useQuery<AlertGroupsReadAll, Error>({
    queryKey: [read.operationId, 'all', query, i18n.language],
    queryFn: ({ signal }) => readAllAlertGroups(scope, (page) => callOperation(read, { query: page }, { language: i18n.language, signal })),
    enabled: query !== null,
  });
  // The places on offer come from the groups read, so the config is rebuilt when they land.
  const config = useMemo(() => alertGroupRankConfig(t, groups.data?.items ?? []), [t, groups.data]);
  const ranked = useRankedCap(groups.data?.items ?? [], config);
  const columns = useGroupColumns(scope, act, now);

  // The grouped queue's inbox (ADR 9042): every group read, the planner's ranking, its top fifty
  // under «Prime 50 di N», the CSV/Excel of every group that matches (ADR 9034).
  return (
    <>
      <GroupedQueueList
        read={groups}
        items={(page) => page.items}
        sentence={inboxSentence}
        columns={columns}
        rowKey={(group) => group.groupKey}
        captionKey="foundations.alertGroups.caption"
        sourceKey="foundations.help.alertGroupsInbox.source"
        loadFailedKey="foundations.alertGroups.loadFailed"
        emptyKey={scope.queue === 'mine' ? 'foundations.alertGroups.emptyMine' : 'foundations.alertGroups.empty'}
        exportId="foundations.alertGroups.table"
        exportTitleKey="foundations.alertGroups.exportTitle"
        ranked={ranked}
        tourAnchor="foundations-exceptions-groups"
      />
      {groups.data?.partial === true && (
        <p className="text-caption text-ink-secondary" data-groups-partial="">
          {t('foundations.alertGroups.partial', { count: groups.data.items.length })}
        </p>
      )}
    </>
  );
}
